import { AlertCircle, MessageSquare } from "lucide-react";
import React, { useState } from "react";
import { TODAY } from "@/data/financial-systems";

const fmtDateTime = (d: Date) =>
  d.toLocaleString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

interface DemoComment {
  id: string;
  actionId: string;
  actionTitle: string;
  authorName: string;
  authorRole: string;
  text: string;
  createdAt: Date;
}

// Standalone demo data — represents comments on SEED_ACTIONS
// (In live mode this report is driven by the backend API)
const NOW = TODAY;
const daysAgo = (n: number) =>
  new Date(NOW.getTime() - n * 24 * 60 * 60 * 1000);
const hoursAgo = (h: number) => new Date(NOW.getTime() - h * 60 * 60 * 1000);

const DEMO_COMMENTS: DemoComment[] = [
  {
    id: "c-001-1",
    actionId: "act-001",
    actionTitle: "Chase overdue invoice — Mersey Build Group",
    authorName: "Edna Foster",
    authorRole: "Accounts Assistant",
    text: "I've sent the first reminder to accounts@merseygroup.co.uk. Awaiting response.",
    createdAt: daysAgo(5),
  },
  {
    id: "c-001-2",
    actionId: "act-001",
    actionTitle: "Chase overdue invoice — Mersey Build Group",
    authorName: "Sarah Mitchell",
    authorRole: "Finance Manager",
    text: "If no response by end of week, escalate to their financial controller directly. I have the contact details.",
    createdAt: daysAgo(4),
  },
  {
    id: "c-001-3",
    actionId: "act-001",
    actionTitle: "Chase overdue invoice — Mersey Build Group",
    authorName: "Edna Foster",
    authorRole: "Accounts Assistant",
    text: "Partial payment of £12,400 received. Remainder (£8,200) now 45 days overdue. Escalating as agreed.",
    createdAt: daysAgo(1),
  },
  {
    id: "c-002-1",
    actionId: "act-002",
    actionTitle: "Review Q1 payroll reconciliation",
    authorName: "Sarah Mitchell",
    authorRole: "Finance Manager",
    text: "Paycircle export shows a £340 variance on the March run. Looks like an NI correction wasn't applied — checking with payroll bureau.",
    createdAt: daysAgo(2),
  },
  {
    id: "c-003-1",
    actionId: "act-003",
    actionTitle: "Confirm equipment hire renewal — JCB backhoe",
    authorName: "Sarah Mitchell",
    authorRole: "Finance Manager",
    text: "Reassigned to Edna — you have the supplier contact for Hewden Plant Hire. Can you confirm the renewal rate and get a quote for an extended term?",
    createdAt: daysAgo(2),
  },
  {
    id: "c-003-2",
    actionId: "act-003",
    actionTitle: "Confirm equipment hire renewal — JCB backhoe",
    authorName: "Edna Foster",
    authorRole: "Accounts Assistant",
    text: "Called Hewden — renewal rate is £820/week vs £795 currently. They can hold the rate for 3 months if we sign this week.",
    createdAt: hoursAgo(6),
  },
  {
    id: "c-004-1",
    actionId: "act-004",
    actionTitle: "Update cash flow model for BigCo revised payment terms",
    authorName: "Sarah Mitchell",
    authorRole: "Finance Manager",
    text: "Updated. BigCo first milestone now on 45-day terms — March low point improves from £125K to £148K. Model shared with Linda.",
    createdAt: hoursAgo(4),
  },
  {
    id: "c-005-1",
    actionId: "act-005",
    actionTitle: "Prepare board pack — cash section",
    authorName: "Linda Kowalski",
    authorRole: "Chief Financial Officer",
    text: "Please include all three growth scenarios (A/B/C) side by side. Catherine wants to see the full range at a glance.",
    createdAt: daysAgo(3),
  },
  {
    id: "c-005-2",
    actionId: "act-005",
    actionTitle: "Prepare board pack — cash section",
    authorName: "Sarah Mitchell",
    authorRole: "Finance Manager",
    text: "Done — A/B/C scenarios on one chart with the base forecast as the reference line. PDF attached to the board SharePoint.",
    createdAt: daysAgo(1),
  },
];

export const ActionsDiscussionAuditPreview: React.FC = () => {
  const [actionFilter, setActionFilter] = useState<string>("all");
  const [authorFilter, setAuthorFilter] = useState<string>("all");
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const reportDate = TODAY.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
  const sorted = [...DEMO_COMMENTS].sort(
    (a, b) => b.createdAt.getTime() - a.createdAt.getTime(),
  );

  const actions = Array.from(new Set(sorted.map((e) => e.actionId))).map(
    (id) => ({
      id,
      title: sorted.find((e) => e.actionId === id)?.actionTitle ?? id,
    }),
  );
  const authors = Array.from(new Set(sorted.map((e) => e.authorName))).sort();

  const filtered = sorted.filter(
    (e) =>
      (actionFilter === "all" || e.actionId === actionFilter) &&
      (authorFilter === "all" || e.authorName === authorFilter),
  );

  return (
    <div className="space-y-6">
      {/* Header strip */}
      <div className="rounded-lg border bg-primary/5 border-primary/20 px-6 py-4 flex items-center justify-between">
        <div>
          <h3 className="font-bold text-lg">Actions Discussion Audit</h3>
          <p className="text-sm text-muted-foreground">
            BuildRight Construction Ltd · All action comments and discussion
            threads
          </p>
        </div>
        <div className="text-right">
          <p className="text-xs text-muted-foreground">Report Date</p>
          <p className="font-semibold">{reportDate}</p>
        </div>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-3 gap-3">
        <div className="rounded-lg border bg-muted/40 p-4 text-center">
          <p className="text-2xl font-bold">{DEMO_COMMENTS.length}</p>
          <p className="text-xs text-muted-foreground mt-1">Total Messages</p>
        </div>
        <div className="rounded-lg border bg-muted/40 p-4 text-center">
          <p className="text-2xl font-bold">{actions.length}</p>
          <p className="text-xs text-muted-foreground mt-1">
            Actions Discussed
          </p>
        </div>
        <div className="rounded-lg border bg-muted/40 p-4 text-center">
          <p className="text-2xl font-bold">{authors.length}</p>
          <p className="text-xs text-muted-foreground mt-1">Contributors</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <label className="text-xs text-muted-foreground font-medium">
            Action:
          </label>
          <select
            value={actionFilter}
            onChange={(e) => setActionFilter(e.target.value)}
            className="text-xs border rounded px-2 py-1 bg-background"
          >
            <option value="all">All actions</option>
            {actions.map((a) => (
              <option key={a.id} value={a.id}>
                {a.title}
              </option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs text-muted-foreground font-medium">
            Author:
          </label>
          <select
            value={authorFilter}
            onChange={(e) => setAuthorFilter(e.target.value)}
            className="text-xs border rounded px-2 py-1 bg-background"
          >
            <option value="all">All authors</option>
            {authors.map((a) => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </select>
        </div>
        {(actionFilter !== "all" || authorFilter !== "all") && (
          <button
            onClick={() => {
              setActionFilter("all");
              setAuthorFilter("all");
            }}
            className="text-xs text-muted-foreground underline hover:no-underline"
          >
            Clear filters
          </button>
        )}
        <span className="text-xs text-muted-foreground ml-auto">
          {filtered.length} message{filtered.length !== 1 ? "s" : ""} shown
        </span>
      </div>

      {/* Table */}
      <div className="rounded-lg border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50">
            <tr>
              <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">
                Date / Time
              </th>
              <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">
                Action
              </th>
              <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">
                Author
              </th>
              <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">
                Role
              </th>
              <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">
                Message
              </th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {filtered.map((entry) => {
              const isExpanded = expandedId === entry.id;
              const preview =
                entry.text.length > 80
                  ? `${entry.text.slice(0, 80)}…`
                  : entry.text;
              return (
                <React.Fragment key={entry.id}>
                  <tr
                    className="hover:bg-muted/30 transition-colors cursor-pointer"
                    onClick={() => setExpandedId(isExpanded ? null : entry.id)}
                  >
                    <td className="px-4 py-2.5 text-muted-foreground whitespace-nowrap">
                      {fmtDateTime(entry.createdAt)}
                    </td>
                    <td className="px-4 py-2.5 font-medium max-w-[180px] truncate">
                      {entry.actionTitle}
                    </td>
                    <td className="px-4 py-2.5">{entry.authorName}</td>
                    <td className="px-4 py-2.5 text-muted-foreground text-xs">
                      {entry.authorRole}
                    </td>
                    <td className="px-4 py-2.5 text-muted-foreground text-xs italic max-w-[240px] truncate">
                      {isExpanded ? entry.text : preview}
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr className="bg-muted/20">
                      <td colSpan={5} className="px-6 py-3">
                        <p className="text-sm text-foreground leading-relaxed">
                          {entry.text}
                        </p>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
            {filtered.length === 0 && (
              <tr>
                <td
                  colSpan={5}
                  className="px-4 py-8 text-center text-sm text-muted-foreground"
                >
                  No messages found for the selected filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Live mode note */}
      <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800 px-4 py-3">
        <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
        <p className="text-xs text-amber-700 dark:text-amber-400">
          In live mode, this report is driven by the backend and covers all
          action comments across your full dataset. The preview above uses demo
          data.
        </p>
      </div>

      {/* Footer */}
      <div className="flex items-center gap-2 text-xs text-muted-foreground border-t pt-4">
        <MessageSquare className="h-3.5 w-3.5" />
        <span>
          {DEMO_COMMENTS.length} total messages across {actions.length} actions
        </span>
      </div>
    </div>
  );
};
